import React, { useMemo } from "react";
import {
  FileText,
  Receipt,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useMyInvoicesQuery } from "../../api/billingApi";
import { formatCurrency, formatDate } from "../../../../utils/formatters";

export default function MyInvoicesPage() {
  const { data: invoices, isLoading } = useMyInvoicesQuery();

  // ✅ Aesthetic Status Logic
  const statusConfig: Record<
    string,
    { label: string; color: string; icon: any }
  > = {
    paid: {
      label: "Settled",
      color: "bg-emerald-50 text-emerald-700 border-emerald-100",
      icon: CheckCircle2,
    },
    partial: {
      label: "Partial",
      color: "bg-amber-50 text-amber-700 border-amber-100",
      icon: Clock,
    },
    unpaid: {
      label: "Outstanding",
      color: "bg-rose-50 text-rose-700 border-rose-100",
      icon: AlertCircle,
    },
    pending: {
      label: "Pending",
      color: "bg-rose-50 text-rose-700 border-rose-100",
      icon: Clock,
    },
    cancelled: {
      label: "Void",
      color: "bg-gray-100 text-gray-500 border-gray-200",
      icon: ShieldCheck,
    },
  };

  // ✅ Summary Stats Calculation
  const stats = useMemo(() => {
    if (!invoices) return { totalBalance: 0, pendingCount: 0 };
    return invoices.reduce(
      (acc: any, inv: any) => ({
        totalBalance: acc.totalBalance + inv.balance,
        pendingCount:
          inv.status !== "paid" ? acc.pendingCount + 1 : acc.pendingCount,
      }),
      { totalBalance: 0, pendingCount: 0 }
    );
  }, [invoices]);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6 flex flex-col items-center justify-center min-h-[400px]">
        <Receipt className="w-12 h-12 text-indigo-500 animate-pulse mb-4" />
        <span className="text-[10px] font-black uppercase tracking-[0.4em] text-gray-400">
          Synchronizing Ledger...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto p-4 md:p-6 pb-20">
      {/* 1. Industrial Header */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <Receipt className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">
                Invoice Registry
              </h1>
              <p className="text-gray-400 text-sm mt-2 font-medium">
                Official billing history and settlement status.
              </p>
            </div>
          </div>

          {/* Quick Stats Grid */}
          <div className="flex gap-4 w-full md:w-auto">
            <div className="bg-gray-50 rounded-3xl p-5 border border-gray-100 flex-1 md:min-w-[200px]">
              <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1 flex items-center gap-2">
                <Wallet className="w-3 h-3" /> Total Outstanding
              </div>
              <div className="text-xl font-black text-gray-900 tracking-tighter">
                {formatCurrency(stats.totalBalance)}
              </div>
            </div>
            <div className="bg-indigo-50/50 rounded-3xl p-5 border border-indigo-100 flex-1 md:min-w-[150px]">
              <div className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mb-1">
                Active Items
              </div>
              <div className="text-xl font-black text-indigo-600 tracking-tighter">
                {stats.pendingCount}{" "}
                <span className="text-xs font-bold text-indigo-300 ml-1">
                  UNPAID
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Invoices List Matrix */}
      {!invoices?.length ? (
        <div className="bg-white border border-gray-100 rounded-[3rem] p-24 text-center">
          <FileText className="w-16 h-16 mx-auto text-gray-100 mb-6" />
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-gray-300">
            No financial records found in your registry
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {invoices.map((inv: any) => {
            const status = statusConfig[inv.status] || statusConfig.cancelled;
            const StatusIcon = status.icon;

            return (
              <div
                key={inv.id}
                className="group bg-white border border-gray-100 rounded-[2.2rem] p-2 hover:shadow-xl hover:shadow-gray-100 transition-all duration-500"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6 p-4 md:p-6">
                  {/* ID & Date Section */}
                  <div className="flex-1 flex items-center gap-6">
                    <div
                      className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${
                        inv.status === "paid"
                          ? "bg-emerald-50 text-emerald-500"
                          : "bg-gray-50 text-gray-400 group-hover:bg-indigo-50 group-hover:text-indigo-500"
                      }`}
                    >
                      <FileText className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-black text-gray-900 uppercase tracking-tight text-lg leading-none">
                          {inv.invoice_number}
                        </h3>
                        <div
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-[9px] font-black uppercase tracking-widest ${status.color}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {status.label}
                        </div>
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 mt-2 uppercase tracking-widest flex items-center gap-2">
                        <Clock className="w-3 h-3" /> Issued:{" "}
                        {inv.created_at ? formatDate(inv.created_at) : "N/A"}
                      </div>
                    </div>
                  </div>

                  {/* Financial Metrics Section */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12 px-2 md:px-8 md:border-x border-gray-50">
                    <div className="space-y-1">
                      <div className="text-[9px] font-black text-gray-300 uppercase tracking-widest">
                        Total Amount
                      </div>
                      <div className="font-bold text-gray-900 text-sm tracking-tight">
                        {formatCurrency(inv.total_amount)}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-[9px] font-black text-gray-300 uppercase tracking-widest">
                        Paid to Date
                      </div>
                      <div className="font-bold text-emerald-600 text-sm tracking-tight">
                        {formatCurrency(inv.amount_paid)}
                      </div>
                    </div>
                    <div className="col-span-2 md:col-span-1 space-y-1">
                      <div className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">
                        Balance Due
                      </div>
                      <div className="font-black text-gray-900 text-lg tracking-tighter leading-none">
                        {formatCurrency(inv.balance)}
                      </div>
                    </div>
                  </div>

                  {/* Action Section */}
                  <div className="flex items-center justify-end">
                    <Link
                      to={`/invoice/${inv.id}`}
                      className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-gray-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg active:scale-95 group/btn"
                    >
                      Invoice Details
                      <ArrowUpRight className="w-3 h-3 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 3. Aesthetic Footer Notification */}
      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center gap-10 shadow-2xl shadow-gray-200 mt-10 overflow-hidden relative">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <ShieldCheck className="w-32 h-32" />
        </div>
        <div className="w-20 h-20 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
          <ShieldCheck className="w-10 h-10 text-indigo-400" />
        </div>
        <div className="relative z-10">
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-3">
            Financial Data Integrity
          </h4>
          <p className="text-sm text-gray-400 leading-relaxed max-w-2xl font-medium">
            This registry displays all official invoices associated with your
            account. All amounts are processed through our{" "}
            <span className="text-white">SECURE PAYMENT GATEWAY</span>. For
            discrepancy reports or payment confirmation delays, please quote the{" "}
            <span className="text-white">AUDIT INVOICE NUMBER</span> to support.
          </p>
        </div>
      </div>
    </div>
  );
}
